import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Film, 
  ArrowRight, 
  CheckCircle, 
  Sparkles,
  PenTool,
  Send,
  Trophy,
  Award,
  Star,
  ChevronDown,
  Instagram,
  Play,
  Clapperboard,
  Tv,
  Users,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { FloatingParticles } from '../components/AnimatedEffects';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Countries list for dropdown
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria",
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia",
  "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
  "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos",
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi",
  "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova",
  "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau",
  "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania",
  "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal",
  "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
  "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
  "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

const GENRES = ["Drama", "Thriller", "Comedy", "Sci-Fi", "Horror", "Action", "Romance", "Other"];

const Challenge = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    moviebotUsername: '',
    screenplayTitle: '',
    genre: '',
    logline: '',
    confirmed: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API_URL}/api/competition/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        const data = await response.json();
        setSubmitError(data.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      q: "Do I need to pay to enter?",
      a: "The competition is free to enter. Submitting requires an active MovieBot subscription (Basic $3.99/mo or Pro $9.99/mo) since your screenplay must be written on our platform."
    },
    {
      q: "What is a short film screenplay?",
      a: "Short film scripts should be 10 to 30 pages, equivalent to approximately 10 to 30 minutes of screen time. Feature-length scripts are not eligible."
    },
    {
      q: "What does 'submitted to streaming' mean exactly?",
      a: "MovieBot will produce the winning screenplay as a short film and formally submit it to streaming platforms including Tubi for distribution consideration. Acceptance is at the platform's discretion. MovieBot does not guarantee placement but will actively advocate for the winning film."
    },
    {
      q: "Who judges the competition?",
      a: "A panel of working screenwriters and film industry professionals. Criteria include premise, structure, character, dialogue, and producibility as a short film."
    },
    {
      q: "Do you take rights to my screenplay?",
      a: "No. You retain full ownership of your work. MovieBot requires a limited production license for the winning screenplay only, agreed with the winner before production begins."
    },
    {
      q: "When is the deadline?",
      a: "End of summer 2026. Exact date to be announced. No extensions."
    },
    {
      q: "Can I enter from outside the United States?",
      a: "Yes. This competition is open to writers in every country."
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Film className="w-6 h-6 text-primary" />
            <span className="font-black text-lg tracking-tight">MOVIEBOT</span>
          </Link>
          <Link to="/signup">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
              Start Writing
            </Button>
          </Link>
        </div>
      </header>

      {/* SECTION 1: HERO */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-20 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(20,184,166,0.15)_0%,_transparent_50%)]" />
        
        {/* Floating particles */}
        <FloatingParticles count={25} color="teal" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 px-4 py-2 mb-8">
                <Globe className="w-4 h-4 text-teal-400" />
                <span className="text-teal-400 font-medium text-sm">Open Worldwide</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-none mb-6">
                The MovieBot<br />
                <span className="text-teal-400">Summer Film Competition</span>
              </h1>
              
              <p className="text-xl md:text-2xl font-bold text-white mb-2">
                Your screenplay. Produced. Submitted to streaming.
              </p>
              
              <p className="text-lg text-muted-foreground mb-4">
                The winner doesn't just get a prize. They get a film.
              </p>
              
              <p className="text-base text-muted-foreground mb-8">
                Free to enter. Open worldwide. Any genre. <span className="text-teal-400 font-bold">Summer 2026.</span>
              </p>

              {/* Summer 2026 styled text (placeholder for countdown) */}
              <div className="inline-flex items-center gap-3 bg-teal-500/10 border border-teal-500/30 px-6 py-4 mb-8">
                <Clapperboard className="w-6 h-6 text-teal-400" />
                <div>
                  <p className="text-xs text-teal-400 font-mono uppercase tracking-wider">Submissions Open</p>
                  <p className="text-2xl font-black text-white">Summer 2026</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                <Link to="/signup">
                  <Button 
                    size="lg"
                    className="bg-teal-500 text-white hover:bg-teal-400 font-bold text-lg px-10 py-6"
                  >
                    Start Writing — It's Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-muted-foreground mt-4 text-center lg:text-left">
                Requires an active MovieBot subscription to submit.
              </p>
            </motion.div>

            {/* Right side - Trophy Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-square max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-b from-teal-500/20 to-transparent rounded-full blur-3xl" />
                <img 
                  src="https://images.pexels.com/photos/6532365/pexels-photo-6532365.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Film Award"
                  className="relative w-full h-full object-cover rounded-2xl border-2 border-teal-500/30"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-2xl" />
                
                {/* Prize overlay */}
                <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-sm border border-teal-500/30 rounded-xl p-4 text-center">
                  <Tv className="w-8 h-8 text-teal-400 mx-auto mb-2" />
                  <p className="text-sm font-bold text-white">Grand Prize</p>
                  <p className="text-xs text-teal-400">Full Production + Streaming Submission</p>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 border-2 border-teal-500/30 rounded-full" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-teal-500 rounded-full blur-xl opacity-30" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: THE OPPORTUNITY */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black mb-8">
              We're not giving away money.<br />
              <span className="text-teal-400">We're giving away something better.</span>
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                Streaming platforms spend <span className="text-white font-semibold">$200 billion a year</span> on original content. They need stories. Great ones come from everywhere — Hollywood, Bollywood, Nollywood, and every city and town in between.
              </p>
              <p>
                MovieBot gives you the tools to write yours. The winning screenplay doesn't sit in a drawer. <span className="text-white font-semibold">We produce it as a short film and submit it directly to streaming platforms</span> for distribution consideration.
              </p>
              <p className="text-xl text-white font-semibold">
                Your vision. Our technology. The world's screen.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: HOW IT WORKS */}
      <section className="py-24 px-6 bg-gradient-to-b from-transparent via-teal-900/10 to-transparent">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-center mb-16">
              Three steps to seeing your film <span className="text-teal-400">on streaming.</span>
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  step: "1",
                  icon: PenTool,
                  title: "Write your screenplay",
                  description: "Use MovieBot's AI-guided 3-act structure to write a complete short film screenplay. No film school required. Our AI walks you scene by scene, beat by beat."
                },
                {
                  step: "2",
                  icon: Send,
                  title: "Submit before the deadline",
                  description: "Complete your script and submit it before the end of summer. One submission per person. Any genre accepted."
                },
                {
                  step: "3",
                  icon: Tv,
                  title: "We produce and submit the winner",
                  description: "Our panel selects the winning script. MovieBot produces it as a short film and submits it to streaming platforms for distribution consideration. The world sees your story."
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white/[0.02] border border-white/10 p-8 hover:border-teal-500/30 transition-colors"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500 flex items-center justify-center text-xl font-black text-white">
                      {item.step}
                    </div>
                    <item.icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4: THE PRIZE */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-center mb-16">
              What the winner <span className="text-teal-400">gets.</span>
            </h2>

            {/* Grand Prize */}
            <div className="bg-gradient-to-br from-teal-500/20 to-emerald-500/10 border-2 border-teal-500/50 p-8 md:p-12 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <Trophy className="w-12 h-12 text-teal-400" />
                <div>
                  <p className="text-sm text-teal-400 font-mono uppercase tracking-wider">Grand Prize</p>
                  <h3 className="text-2xl md:text-3xl font-black text-white">Full Short Film Production + Streaming Submission</h3>
                </div>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span>Your screenplay produced as a complete short film</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span>AI-assisted production including scenes, voiceover, original soundtrack, and post-production</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span>Submitted to streaming platforms including Tubi for distribution consideration</span>
                </li>
              </ul>
            </div>

            {/* Runner-Up and Genre Awards */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/[0.02] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Award className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-sm text-amber-400 font-mono">Runner-Up × 5</p>
                    <h3 className="text-lg font-bold text-white">Best in Category</h3>
                  </div>
                </div>
                <p className="text-muted-foreground">1 Year MovieBot Pro subscription</p>
              </div>

              <div className="bg-white/[0.02] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Star className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-sm text-purple-400 font-mono">Genre Awards × 5</p>
                    <h3 className="text-lg font-bold text-white">Lifetime MovieBot Pro</h3>
                  </div>
                </div>
                <p className="text-muted-foreground">Drama, Thriller, Comedy, Sci-Fi, Horror</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 5: ENTRY REQUIREMENTS */}
      <section className="py-24 px-6 bg-gradient-to-b from-transparent via-teal-900/10 to-transparent">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-center mb-12">
              How to <span className="text-teal-400">qualify.</span>
            </h2>

            <div className="space-y-4">
              {[
                "Write a complete short film screenplay using MovieBot (10 to 30 pages)",
                "Hold an active MovieBot Basic or Pro subscription at time of submission",
                "Follow @moviebot.tech on Instagram and TikTok",
                "Share this competition publicly, tag @moviebot.tech, and use #MovieBotSummer",
                "One submission per person. Original unpublished work only."
              ].map((req, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-start gap-4 p-4 bg-white/[0.02] border border-white/10"
                >
                  <CheckCircle className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white">{req}</span>
                </motion.div>
              ))}
            </div>

            <p className="text-center text-muted-foreground mt-8">
              Open to writers in all countries. Subscription required for screenplay submission only.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 6: SUBMISSION FORM */}
      <section id="submit" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-center mb-4">
              Enter the <span className="text-teal-400">competition.</span>
            </h2>
            <p className="text-center text-muted-foreground mb-12">
              Submit your screenplay before the deadline.
            </p>

            {submitSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-teal-500/20 border border-teal-500/50 p-8 text-center"
              >
                <Sparkles className="w-12 h-12 text-teal-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Entry Received!</h3>
                <p className="text-muted-foreground">
                  Your entry has been received. We will be in touch at <span className="text-white font-semibold">{formData.email}</span>.
                </p>
                <p className="text-teal-400 font-semibold mt-4">This is your summer. 🎬</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name fields */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-teal-500 focus:outline-none transition-colors"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-teal-500 focus:outline-none transition-colors"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-teal-500 focus:outline-none transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Country *</label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white focus:border-teal-500 focus:outline-none transition-colors"
                  >
                    <option value="" className="bg-[#0a0a0a]">Select your country</option>
                    {COUNTRIES.map(country => (
                      <option key={country} value={country} className="bg-[#0a0a0a]">{country}</option>
                    ))}
                  </select>
                </div>

                {/* MovieBot Username */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">MovieBot Username or Account Email *</label>
                  <input
                    type="text"
                    name="moviebotUsername"
                    value={formData.moviebotUsername}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-teal-500 focus:outline-none transition-colors"
                    placeholder="Your MovieBot account"
                  />
                </div>

                {/* Screenplay Title */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Screenplay Title *</label>
                  <input
                    type="text"
                    name="screenplayTitle"
                    value={formData.screenplayTitle}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-teal-500 focus:outline-none transition-colors"
                    placeholder="Your screenplay title"
                  />
                </div>

                {/* Genre */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Genre *</label>
                  <select
                    name="genre"
                    value={formData.genre}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white focus:border-teal-500 focus:outline-none transition-colors"
                  >
                    <option value="" className="bg-[#0a0a0a]">Select genre</option>
                    {GENRES.map(genre => (
                      <option key={genre} value={genre} className="bg-[#0a0a0a]">{genre}</option>
                    ))}
                  </select>
                </div>

                {/* Logline */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Logline * <span className="text-muted-foreground font-normal">({formData.logline.length}/150)</span>
                  </label>
                  <textarea
                    name="logline"
                    value={formData.logline}
                    onChange={(e) => {
                      if (e.target.value.length <= 150) {
                        handleInputChange(e);
                      }
                    }}
                    required
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-teal-500 focus:outline-none transition-colors resize-none"
                    placeholder="A one-sentence summary of your screenplay (150 characters max)"
                  />
                  <div className="h-1 bg-white/10 mt-2">
                    <div 
                      className={`h-full transition-all ${formData.logline.length > 140 ? 'bg-amber-500' : 'bg-teal-500'}`}
                      style={{ width: `${(formData.logline.length / 150) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Confirmation Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="confirmed"
                    checked={formData.confirmed}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-5 h-5 accent-teal-500"
                  />
                  <label className="text-sm text-muted-foreground">
                    I confirm this screenplay was written using MovieBot, I hold an active subscription, I have followed @moviebot.tech on Instagram and TikTok, and I have shared this competition publicly with #MovieBotSummer *
                  </label>
                </div>

                {submitError && (
                  <div className="bg-red-500/20 border border-red-500/50 p-4 text-red-400 text-sm">
                    {submitError}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 text-white hover:bg-teal-400 font-bold text-lg py-6"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit My Entry'}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* SECTION 7: FAQ */}
      <section className="py-24 px-6 bg-gradient-to-b from-transparent via-teal-900/10 to-transparent">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-center mb-12">
              Frequently Asked <span className="text-teal-400">Questions</span>
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <span className="font-semibold text-white pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-teal-400 flex-shrink-0 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-muted-foreground">{faq.a}</p>
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 8: FOOTER CTA */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Clapperboard className="w-16 h-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              Your story deserves to be <span className="text-teal-400">seen.</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Not sitting in a drawer. On a screen. This summer.
            </p>
            
            <Link to="/signup">
              <Button size="lg" className="bg-teal-500 text-white hover:bg-teal-400 font-bold text-lg px-10 py-6">
                Write My Screenplay — Free to Start
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            
            <p className="text-sm text-muted-foreground mt-4">
              Free to start. $3.99/mo to compete. Priceless to win.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Social Footer */}
      <footer className="py-12 px-6 bg-white/[0.02] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Film className="w-5 h-5 text-primary" />
              <span className="font-bold">MOVIEBOT</span>
              <span className="text-muted-foreground text-sm ml-2">© 2026</span>
            </div>
            
            <div className="flex items-center gap-6">
              <a 
                href="https://instagram.com/moviebot.tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
                <span className="text-sm">@moviebot.tech</span>
              </a>
              <a 
                href="https://tiktok.com/@moviebot.tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                <span className="text-sm">@moviebot.tech</span>
              </a>
            </div>
            
            <a href="https://moviebot.tech" className="text-sm text-muted-foreground hover:text-white transition-colors">
              moviebot.tech
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Challenge;
